import { useState } from "react";
import { Link } from "react-router-dom";
import type { Book } from "../api/types";
import { useAuthedImage } from "../hooks/useAuthedImage";
import { coverColorFor } from "../lib/cover";
import { formatDuration } from "../lib/format";
import StatusChip from "./StatusChip";

export default function BookCard({ book }: { book: Book }) {
  const title = book.book_title ?? book.video_title ?? "Untitled video";
  // Thumbnail first: it is the image the user recognizes the video by, and the
  // generated typographic cover (dark ground, or blank for scripts the cover
  // font cannot draw) made completion look like the cover "disappeared".
  // The generated cover stays on the EPUB/PDF and serves as the fallback here.
  const [failed, setFailed] = useState<string | null>(null);
  const candidates = [book.thumbnail_url, book.cover_url].filter(
    (src): src is string => Boolean(src) && src !== failed
  );
  // The API-served cover needs fetching with credentials; the YouTube
  // thumbnail does not. useAuthedImage handles both.
  const imgSrc = useAuthedImage(candidates[0] ?? null);

  return (
    <Link
      to={`/books/${book.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-border bg-surface card-shadow transition-transform hover:-translate-y-0.5 focus-visible:-translate-y-0.5"
    >
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-surface-raised">
        {imgSrc ? (
          <img src={imgSrc} alt="" loading="lazy" onError={() => setFailed(candidates[0] ?? null)} className="h-full w-full object-cover p-[5px]" />
        ) : imgSrc === undefined ? null : (
          <GeneratedCover videoId={book.video_id} title={title} channel={book.channel} />
        )}
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-3.5">
        <h3 className="line-clamp-2 font-serif text-base font-semibold leading-snug text-text">{title}</h3>
        <p className="line-clamp-1 text-xs text-muted">
          {book.channel ?? "Unknown channel"}
          {book.duration_s != null ? ` · ${formatDuration(book.duration_s)}` : ""}
        </p>
        <div className="mt-auto pt-2">
          <StatusChip book={book} />
        </div>
      </div>
    </Link>
  );
}

function GeneratedCover({ videoId, title, channel }: { videoId: string; title: string; channel: string | null }) {
  const color = coverColorFor(videoId);
  return (
    <div
      className="flex h-full w-full flex-col justify-between p-4 text-center"
      style={{ backgroundColor: color }}
    >
      <div aria-hidden />
      <p className="line-clamp-4 font-serif text-sm font-semibold leading-snug text-white/95">{title}</p>
      <div className="space-y-1">
        {channel ? <p className="line-clamp-1 text-[10px] text-white/70">{channel}</p> : null}
        <p className="text-[9px] uppercase tracking-[0.2em] text-white/50">Boort</p>
      </div>
    </div>
  );
}
